import { useDidUpdate } from "app/custom-hooks/useDidUpdate";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import { useEffect, useMemo, useState } from "react";
import BaseActivatedPanel from "../../../../table-actions-panel/components/BaseActivatedPanel/BaseActivatedPanel";
import {
    StyledInnerContainer,
    StyledPanelIcon,
} from "../../../../table-actions-panel/components/BaseActivatedPanel/styles";
import type { BaseActivatedPanelProps } from "../../../../table-actions-panel/components/BaseActivatedPanel/types";
import SelectionDDItem from "../../../../table-actions-panel/components/SelectionDDItem/SelectionDDItem";
import ManualSelectionItem from "../../../../table-actions-panel/models/selection-dropdown-item/ManualSelectionItem/ManualSelectionItem";
import type { SelectionDropdownItem } from "../../../../table-actions-panel/models/selection-dropdown-item/types";
import removeFromListSelectionDropdown from "../../../services/table-actions-panel/remove-from-list/removeFromListSelectionDropdown";
import { StyledRemoveSubmitButton } from "../RemoveSubmitButton/styles";
import { StyledSelectionDropdown, StyledTitle } from "./styles";

type ActivatedRemoveFromListPanelProps = BaseActivatedPanelProps & {
    isLoading: boolean;
    isDisabled: boolean;
    numberOfResults: number;
    selectedIds: readonly string[];
    onSubmit(activeSelectionItem: Readonly<SelectionDropdownItem>): void;
    onSelectionChange(item: Readonly<SelectionDropdownItem>): void;
};

const ActivatedRemoveFromListPanel = (props: ActivatedRemoveFromListPanelProps) => {
    const { isDisabled, isLoading, numberOfResults, selectedIds, onSelectionChange, onSubmit } = props;
    const manualSelectionItem = new ManualSelectionItem(selectedIds.length);

    const dropdownItems = useMemo(() => {
        return removeFromListSelectionDropdown.getItems(numberOfResults);
    }, [numberOfResults]);

    const [activeSelectionItem, setActiveSelectionItem] = useState<SelectionDropdownItem>(manualSelectionItem);

    useEffect(() => {
        if (activeSelectionItem.id !== ManualSelectionItem.ID) {
            setActiveSelectionItem(manualSelectionItem);
        }
    }, [dropdownItems]);

    useEffect(() => {
        onSelectionChange(activeSelectionItem);
    }, [activeSelectionItem.id]);

    useDidUpdate(() => {
        if (!isLoading) {
            onSelectionChange(activeSelectionItem);
        }
    }, [isLoading]);

    useEffect(() => {
        if (activeSelectionItem.id === ManualSelectionItem.ID) {
            setActiveSelectionItem(manualSelectionItem);
        }
    }, [selectedIds.length]);

    function handleSubmit() {
        onSubmit(activeSelectionItem);
    }

    function getIsSubmitDisabled() {
        if (isDisabled) {
            return isDisabled;
        }

        if (activeSelectionItem.id === ManualSelectionItem.ID) {
            return selectedIds.length === 0;
        }

        return false;
    }

    return (
        <BaseActivatedPanel onDeactivate={props.onDeactivate}>
            <StyledInnerContainer>
                <StyledPanelIcon
                    iconName="delete"
                    size="xs"
                />
                <I18n
                    element={StyledTitle}
                    data-automation="actions-panel.remove-label"
                    translatable={{ key: "si.multi_selector.remove.title" }}
                />
                <StyledSelectionDropdown
                    isLoading={isLoading}
                    buttonText={activeSelectionItem.getSelectedTranslation()}
                    onSelect={setActiveSelectionItem}
                >
                    {dropdownItems
                        .map((item) => (
                            <SelectionDDItem
                                key={item.id}
                                item={item}
                                isSelected={activeSelectionItem.id === item.id}
                            />
                        ))
                        .concat(
                            <SelectionDDItem
                                item={manualSelectionItem}
                                key={ManualSelectionItem.ID}
                                isSelected={activeSelectionItem.id === ManualSelectionItem.ID}
                            />,
                        )}
                </StyledSelectionDropdown>
                <StyledRemoveSubmitButton
                    isDisabled={getIsSubmitDisabled()}
                    isLoading={isLoading}
                    onClick={handleSubmit}
                />
            </StyledInnerContainer>
        </BaseActivatedPanel>
    );
}

export default ActivatedRemoveFromListPanel;
